import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import CartDrawer from '../home/components/CartDrawer';
import Footer from '../home/components/Footer';
import { useWishlist } from '../../hooks/useWishlist';
import { useCart } from '../../hooks/useCart';
import { useSEO } from '../../hooks/useSEO';

export default function WishlistPage() {
  const navigate = useNavigate();
  const { items, removeItem } = useWishlist();
  const { addItem } = useCart();
  const [addedId, setAddedId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [cartToast, setCartToast] = useState(false);
  const [bulkAdded, setBulkAdded] = useState(false);
  const [bulkToastCount, setBulkToastCount] = useState(0);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Wishlist | Motion Boyz – Your Saved Pieces',
    description: 'View and manage your saved Motion Boyz pieces. Your personal collection of luxury streetwear favourites, ready to add to cart whenever you are.',
    canonical: '/wishlist',
    ogImage: `${siteUrl}/og-wishlist.jpg`,
    ogType: 'website',
    jsonLd: {
      '@type': 'WebPage',
      name: 'Wishlist – Motion Boyz',
      description: 'Your saved Motion Boyz luxury streetwear pieces.',
      url: `${siteUrl}/wishlist`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Motion Boyz',
        url: siteUrl,
      },
    },
  });

  const extractNumericId = (gid: string) => gid.split('/').pop() || '';

  const handleRemove = (id: string) => {
    setRemovingId(id);
    setTimeout(() => {
      removeItem(id);
      setRemovingId(null);
    }, 350);
  };

  const handleAddToCart = (item: typeof items[0], e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.variantId) return;
    addItem({
      id: item.variantId,
      variantId: item.variantId,
      name: item.name,
      price: item.price,
      image: item.image,
    });
    setAddedId(item.id);
    setCartToast(true);
    setTimeout(() => setAddedId(null), 1800);
    setTimeout(() => setCartToast(false), 2400);
  };

  const handleMoveAllToCart = () => {
    const available = items.filter((item) => item.availableForSale && item.variantId);
    if (available.length === 0) return;
    available.forEach((item) => {
      addItem({
        id: item.variantId!,
        variantId: item.variantId!,
        name: item.name,
        price: item.price,
        image: item.image,
      });
    });
    setBulkToastCount(available.length);
    setBulkAdded(true);
    setCartToast(true);
    setTimeout(() => setBulkAdded(false), 2200);
    setTimeout(() => setCartToast(false), 2800);
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-black">

        {/* Hero Banner */}
        <div className="relative w-full h-[400px] overflow-hidden pt-16">
          <img
            src="https://public.readdy.ai/ai/img_res/92acec01e7208e54888d076d2e7f2932.jpg"
            alt="Motion Boyz Wishlist – City Skyline"
            className="w-full h-full object-cover object-center"
            style={{ transformOrigin: 'center center' }}
          />
          {/* Dark base overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black"></div>
          {/* Cyan glow from bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#00d4ff]/10 to-transparent"></div>
          {/* Rose glow accent */}
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 80%, rgba(244,63,94,0.08) 0%, transparent 70%)' }}></div>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="inline-flex items-center gap-2 px-4 py-1 border border-rose-500/40 rounded-full mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse"></span>
              <span className="text-rose-400 text-xs tracking-widest uppercase">Your Saved Pieces</span>
            </div>
            <h1
              className="text-6xl md:text-7xl font-black tracking-widest uppercase mb-4 text-white"
              style={{ fontFamily: 'Rajdhani, sans-serif', textShadow: '0 0 40px rgba(244,63,94,0.4), 0 0 80px rgba(0,212,255,0.15)' }}
            >
              Wishlist
            </h1>
            <p className="text-gray-300 text-sm tracking-wider max-w-xl">
              {items.length > 0
                ? `${items.length} ${items.length === 1 ? 'piece' : 'pieces'} saved — ready when you are.`
                : 'Your curated collection of favourites lives here.'}
            </p>
          </div>
        </div>

        <div className="w-[90%] max-w-[1400px] mx-auto pb-24 pt-10">

          {/* Empty State */}
          {items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-32 gap-6">
              <div
                className="w-24 h-24 flex items-center justify-center rounded-full border border-rose-500/20"
                style={{ boxShadow: '0 0 40px rgba(244,63,94,0.08)' }}
              >
                <i className="ri-heart-line text-5xl text-rose-400/40"></i>
              </div>
              <div className="text-center">
                <p className="text-white text-lg font-semibold tracking-wide mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  Nothing saved yet
                </p>
                <p className="text-gray-500 text-sm tracking-wider max-w-sm">
                  Browse the collection and tap the heart icon on any piece to save it here.
                </p>
              </div>
              <button
                onClick={() => navigate('/shop')}
                className="mt-2 px-8 py-3 bg-[#00d4ff] text-black text-xs tracking-widest uppercase font-semibold rounded-full hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
                style={{ boxShadow: '0 0 20px rgba(0,212,255,0.4)' }}
              >
                <i className="ri-shopping-bag-line mr-2"></i>Explore Collection
              </button>
            </div>
          )}

          {/* Wishlist Grid */}
          {items.length > 0 && (
            <>
              {/* Header row */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <span className="w-1 h-4 bg-rose-400 rounded-full"></span>
                  <p className="text-gray-400 text-xs tracking-widest uppercase">
                    {items.length} {items.length === 1 ? 'Saved Piece' : 'Saved Pieces'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Move All to Cart */}
                  {items.some((i) => i.availableForSale && i.variantId) && (
                    <button
                      onClick={handleMoveAllToCart}
                      className={`flex items-center gap-2 px-5 py-2 text-xs tracking-widest uppercase font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        bulkAdded
                          ? 'bg-green-400 text-black border border-green-400'
                          : 'bg-[#00d4ff] text-black hover:bg-white'
                      }`}
                      style={!bulkAdded ? { boxShadow: '0 0 18px rgba(0,212,255,0.4)' } : {}}
                    >
                      {bulkAdded ? (
                        <>
                          <i className="ri-check-line text-sm"></i>All Added!
                        </>
                      ) : (
                        <>
                          <i className="ri-shopping-cart-2-line text-sm"></i>
                          Move All to Cart
                          <span className="ml-1 px-1.5 py-0.5 bg-black/20 rounded-full text-[10px]">
                            {items.filter((i) => i.availableForSale && i.variantId).length}
                          </span>
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/shop')}
                    className="flex items-center gap-2 px-4 py-2 border border-[#00d4ff]/20 text-[#00d4ff] text-xs tracking-widest uppercase rounded-full hover:bg-[#00d4ff]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line text-sm"></i>Add More
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={`group relative bg-gradient-to-br from-[#0d0d0d] to-[#060606] border border-white/5 rounded-2xl overflow-hidden transition-all duration-500 hover:border-rose-500/30 hover:shadow-[0_0_40px_rgba(244,63,94,0.1)] ${
                      removingId === item.id ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                    }`}
                    style={{ transition: 'opacity 0.35s ease, transform 0.35s ease, border-color 0.5s, box-shadow 0.5s' }}
                  >
                    {/* Image */}
                    <div
                      className="relative w-full h-72 overflow-hidden bg-[#0a0a0a] cursor-pointer"
                      onClick={() => navigate(`/product/${extractNumericId(item.id)}`)}
                    >
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-700">
                          <i className="ri-image-line text-5xl"></i>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                      {/* Availability badge */}
                      {!item.availableForSale && (
                        <div className="absolute bottom-3 left-3 px-2 py-0.5 bg-black/80 border border-white/20 rounded-full">
                          <span className="text-gray-400 text-[9px] tracking-widest uppercase">Sold Out</span>
                        </div>
                      )}

                      {/* Remove button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemove(item.id); }}
                        aria-label="Remove from wishlist"
                        className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-rose-500/90 border border-rose-400 text-white shadow-[0_0_14px_rgba(244,63,94,0.5)] hover:bg-rose-600 transition-all duration-300 cursor-pointer whitespace-nowrap z-10"
                      >
                        <i className="ri-heart-fill text-sm"></i>
                      </button>

                      {/* View detail overlay */}
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/product/${extractNumericId(item.id)}`); }}
                          className="px-5 py-2 text-xs tracking-widest uppercase font-semibold rounded-full bg-white/10 border border-white/30 text-white hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm"
                        >
                          <i className="ri-eye-line mr-1.5"></i>View Details
                        </button>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-5">
                      {item.category && (
                        <p className="text-rose-400/70 text-[10px] tracking-widest uppercase mb-1.5">
                          {item.category}
                        </p>
                      )}
                      <h3
                        className="text-white text-sm font-semibold mb-3 tracking-wide leading-snug cursor-pointer hover:text-rose-300 transition-colors duration-300"
                        onClick={() => navigate(`/product/${extractNumericId(item.id)}`)}
                      >
                        {item.name}
                      </h3>
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-gray-300 text-sm font-light">
                          ${item.price.toFixed(2)}{' '}
                          <span className="text-gray-600 text-xs">{item.currencyCode}</span>
                        </p>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemove(item.id); }}
                          className="text-gray-600 hover:text-rose-400 text-xs tracking-wider transition-colors duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>Remove
                        </button>
                      </div>

                      {/* Add to Cart */}
                      <button
                        onClick={(e) => handleAddToCart(item, e)}
                        disabled={!item.availableForSale || !item.variantId}
                        className={`w-full py-2.5 rounded-full text-xs font-semibold tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap disabled:bg-gray-800 disabled:text-gray-500 disabled:cursor-not-allowed ${
                          addedId === item.id
                            ? 'bg-green-400 text-black'
                            : item.availableForSale
                            ? 'bg-[#00d4ff] text-black hover:bg-white'
                            : ''
                        }`}
                        style={addedId !== item.id && item.availableForSale ? { boxShadow: '0 0 16px rgba(0,212,255,0.35)' } : {}}
                      >
                        {addedId === item.id
                          ? '✓ Added to Cart'
                          : item.availableForSale
                          ? 'Add to Cart'
                          : 'Sold Out'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => navigate('/shop')}
                  className="px-8 py-3 border border-[#00d4ff]/30 text-[#00d4ff] text-xs tracking-widest uppercase font-semibold rounded-full hover:bg-[#00d4ff]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>Continue Shopping
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
      <CartDrawer />

      {/* Cart Toast */}
      <div
        className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[300] px-5 py-3 rounded-full bg-[#111] border border-[#00d4ff]/40 text-white text-xs tracking-widest uppercase shadow-[0_0_20px_rgba(0,212,255,0.3)] flex items-center gap-2 transition-all duration-400 whitespace-nowrap ${
          cartToast ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
        }`}
      >
        <i className="ri-shopping-cart-line text-[#00d4ff]"></i>
        {bulkToastCount > 1 ? `${bulkToastCount} pieces added to cart` : 'Added to cart'}
      </div>
    </>
  );
}
